import {
  Action,
  ActionExample,
  HandlerCallback,
  IAgentRuntime,
  Memory,
  ModelType,
  State,
  logger,
} from '@elizaos/core';
import { ZapperConfig } from '../types';
import {
  AccountInfoContent,
  AccountInfoData,
  AccountInfoResponse,
  AccountResolutionResult,
  AccountSocialLink,
  AccountDisplayNameSource,
  AccountAvatarSource,
  FarcasterProfileResponse,
  FarcasterProfileData,
} from '../types/account';
import {
  getZapperClient,
  initializeZapperClient,
  isZapperClientInitialized,
} from '../utils/zapperClient';

export const getAccountInfoAction: Action = {
  name: 'GET_ACCOUNT_INFO',
  similes: [
    'ACCOUNT_INFO',
    'RESOLVE_ACCOUNT',
    'LOOKUP_ACCOUNT',
    'GET_PROFILE',
    'RESOLVE_ENS',
    'RESOLVE_FARCASTER',
    'ACCOUNT_RESOLUTION',
    'PROFILE_LOOKUP',
    'SOCIAL_PROFILE',
    'USER_INFO',
  ],
  description:
    'Get comprehensive account information including ENS, Farcaster, Lens profiles, and social data for addresses or usernames',
  validate: async (runtime: IAgentRuntime, message: Memory, state?: State): Promise<boolean> => {
    logger.info('🔍 Starting GET_ACCOUNT_INFO validation', {
      messageId: message.id,
      messageText: message.content.text?.substring(0, 100) + '...',
      hasState: !!state,
    });

    const hasApiKey = runtime.getSetting('ZAPPER_API_KEY');
    if (!hasApiKey) {
      logger.error('❌ Validation failed: ZAPPER_API_KEY not configured');
      return false;
    }
    logger.debug('✅ ZAPPER_API_KEY is configured');

    const messageText = message.content.text || '';
    logger.debug('📝 Processing message text for identifier extraction', {
      textLength: messageText.length,
      textPreview: messageText.substring(0, 50) + '...',
    });

    // Use LLM for validation - quick check
    const extractedIdentifier = await extractAccountIdentifier(runtime, messageText, state);

    if (!extractedIdentifier) {
      logger.warn('⚠️ No account identifier found in message or state for GET_ACCOUNT_INFO', {
        messageText: messageText.substring(0, 100),
        hasState: !!state,
      });
      return false;
    }

    logger.info('✅ Validation passed - identifier found', {
      identifier: extractedIdentifier.value,
      type: extractedIdentifier.type,
    });
    return true;
  },
  handler: async (
    runtime: IAgentRuntime,
    message: Memory,
    state?: State,
    options?: any,
    callback?: HandlerCallback
  ) => {
    logger.info('🚀 Starting GET_ACCOUNT_INFO handler', {
      messageId: message.id,
      hasState: !!state,
      options: options,
    });

    try {
      // Extract account identifier from message text or state
      const messageText = message.content.text || '';
      logger.debug('📝 Extracting account identifier from message/state', {
        messageLength: messageText.length,
        hasState: !!state,
      });

      const extractedIdentifier = await extractAccountIdentifier(runtime, messageText, state);

      if (!extractedIdentifier) {
        logger.error('❌ No account identifier found for lookup', {
          messageText: messageText.substring(0, 100),
          hasState: !!state,
        });
        callback?.({
          text: 'I need a wallet address, ENS name, basename, or Farcaster username to fetch account information. Please provide one of these identifiers.',
          content: { error: 'No account identifier provided' },
        });
        return;
      }

      logger.info('📍 Account identifier extracted successfully', {
        identifier: extractedIdentifier.value,
        type: extractedIdentifier.type,
      });

      // Create content object based on identifier type
      const content = createContentFromIdentifier(extractedIdentifier);

      logger.info('📊 Content created from identifier', {
        content,
        hasAddress: !!content.address,
        hasEns: !!content.ens,
        hasBasename: !!content.basename,
        hasFarcasterUsername: !!content.farcasterUsername,
        hasFid: !!content.fid,
      });

      const apiKey = runtime.getSetting('ZAPPER_API_KEY');
      if (!apiKey) {
        logger.error('❌ ZAPPER_API_KEY not configured in runtime settings');
        callback?.({
          text: 'Failed to fetch account information. Please try again.',
          content: {
            error: 'ZAPPER_API_KEY not configured',
          },
        });
        return;
      }

      const apiUrl = runtime.getSetting('ZAPPER_API_URL') || 'https://public.zapper.xyz/graphql';
      const zapperConfig: ZapperConfig = {
        apiKey,
        apiUrl,
      };
      logger.info('🔧 Zapper configuration prepared', {
        apiUrl,
        hasApiKey: !!apiKey,
      });

      logger.info('🌐 Fetching account information from Zapper API', { content });
      const accountInfo = await fetchAccountInfo(zapperConfig, content);
      logger.info('📡 Account information fetch completed', {
        found: !!accountInfo,
        address: accountInfo?.address,
      });

      if (!accountInfo) {
        logger.warn('⚠️ No account information found for provided identifiers', {
          searchCriteria: {
            address: content.address,
            ens: content.ens,
            basename: content.basename,
            farcasterUsername: content.farcasterUsername,
            fid: content.fid,
          },
        });
        callback?.({
          text: `No account information found for the provided identifier`,
          content: {
            error: 'Account not found',
            action: 'GET_ACCOUNT_INFO',
            address: content.address,
            ensName: content.ens,
            basename: content.basename,
            farcasterUsername: content.farcasterUsername,
            fid: content.fid,
          },
        });
        return;
      }

      // Format the response
      logger.debug('🎨 Formatting account information response', {
        socialLinksCount: accountInfo.socialLinks?.length || 0,
        badgesCount: accountInfo.badges?.length || 0,
      });

      const socialLinks = accountInfo.socialLinks
        .map((link) => `${link.name}: ${link.url}`)
        .join('\\n');

      const responseText = `Account Information:

👤 **${accountInfo.displayName}**
📍 Address: ${accountInfo.address}
${accountInfo.ens ? `🌐 ENS: ${accountInfo.ens}` : ''}
${accountInfo.basename ? `🔵 Basename: ${accountInfo.basename}` : ''}
${accountInfo.farcasterUsername ? `🟣 Farcaster: @${accountInfo.farcasterUsername} (FID: ${accountInfo.fid})` : ''}
${accountInfo.description ? `📝 ${accountInfo.description}` : ''}

${accountInfo.followStats ? `👥 Followers: ${accountInfo.followStats.followerCount} | Following: ${accountInfo.followStats.followingCount}` : ''}

${socialLinks ? `🔗 Social Links:\\n${socialLinks}` : ''}

${accountInfo.isContract ? '⚙️ Contract Account' : '👤 EOA (Externally Owned Account)'}

${accountInfo.badges && accountInfo.badges.length > 0 ? `🏆 Badges: ${accountInfo.badges.map((b) => b.badgeName).join(', ')}` : ''}`;

      logger.info('✅ Successfully processed account information request', {
        address: accountInfo.address,
        displayName: accountInfo.displayName,
        hasEns: !!accountInfo.ens,
        hasBasename: !!accountInfo.basename,
        hasFarcaster: !!accountInfo.farcasterUsername,
        isContract: accountInfo.isContract,
      });

      callback?.({
        text: responseText,
        content: {
          action: 'GET_ACCOUNT_INFO',
          address: accountInfo.address,
          ens: accountInfo.ens,
          basename: accountInfo.basename,
          farcasterUsername: accountInfo.farcasterUsername,
          fid: accountInfo.fid,
          displayName: accountInfo.displayName,
          description: accountInfo.description,
          socialLinks: accountInfo.socialLinks,
          followStats: accountInfo.followStats,
          isContract: accountInfo.isContract,
          badges: accountInfo.badges,
        },
      });
      return;
    } catch (error) {
      logger.error('❌ GET_ACCOUNT_INFO handler failed with error', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        messageId: message.id,
      });
      callback?.({
        text: 'Failed to fetch account information. Please try again.',
        content: {
          error: error instanceof Error ? error.message : String(error),
        },
      });
      return;
    }
  },
  examples: [
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Get account info for vitalik.eth',
        },
      },
      {
        name: '{{user2}}',
        content: {
          text: 'Account information retrieved successfully',
          action: 'GET_ACCOUNT_INFO',
        },
      },
    ],
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Look up Farcaster profile for @dwr',
        },
      },
      {
        name: '{{user2}}',
        content: {
          text: 'Farcaster profile information retrieved',
          action: 'GET_ACCOUNT_INFO',
        },
      },
    ],
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Resolve address 0x1234567890abcdef1234567890abcdef12345678',
        },
      },
      {
        name: '{{user2}}',
        content: {
          text: 'Address resolved with social profiles',
          action: 'GET_ACCOUNT_INFO',
        },
      },
    ],
  ],
};

async function fetchAccountInfo(
  config: ZapperConfig,
  content: AccountInfoContent
): Promise<AccountResolutionResult | null> {
  logger.info('🌐 Starting fetchAccountInfo', {
    hasAddress: !!content.address,
    hasEns: !!content.ens,
    hasBasename: !!content.basename,
    hasFarcasterUsername: !!content.farcasterUsername,
    hasFid: !!content.fid,
  });

  // Initialize client if not already done
  if (!isZapperClientInitialized()) {
    logger.debug('🔧 Initializing Zapper client', { apiUrl: config.apiUrl });
    initializeZapperClient(config.apiKey, config.apiUrl);
  } else {
    logger.debug('✅ Zapper client already initialized');
  }

  const client = getZapperClient();
  logger.debug('📡 Zapper client retrieved successfully');

  // Farcaster profile resolution
  if (content.farcasterUsername || content.fid) {
    logger.info('🟣 Farcaster profile resolution initiated', {
      username: content.farcasterUsername,
      fid: content.fid,
    });

    // Determine which identifier to use based on what we have and query analysis
    let useFid: number | string | undefined;
    let useUsername: string | undefined;

    if (content.fid && (typeof content.fid === 'number' || typeof content.fid === 'string')) {
      useFid = content.fid;
      logger.debug('🎯 Prioritizing FID for Farcaster lookup', { fid: useFid });
    } else if (content.farcasterUsername && typeof content.farcasterUsername === 'string') {
      useUsername = content.farcasterUsername;
      logger.debug('🎯 Using username for Farcaster lookup', { username: useUsername });
    } else {
      logger.warn('⚠️ Invalid Farcaster identifiers provided', {
        fid: content.fid,
        fidType: typeof content.fid,
        username: content.farcasterUsername,
        usernameType: typeof content.farcasterUsername,
      });
    }

    if (useFid || useUsername) {
      const farcasterProfile = await fetchFarcasterProfile(config, useUsername, useFid);

      if (farcasterProfile) {
        logger.info('✅ Farcaster profile resolved successfully', {
          username: farcasterProfile.username,
          fid: farcasterProfile.fid,
          custodyAddress: farcasterProfile.custodyAddress,
          connectedAddressesCount: farcasterProfile.connectedAddresses?.length || 0,
        });

        // Now fetch account info using the resolved addresses
        const addresses = [farcasterProfile.custodyAddress, ...farcasterProfile.connectedAddresses];
        logger.debug('🔍 Fetching account info for resolved addresses', { addresses });

        const accountInfo = await fetchAccountsByAddresses(config, addresses);

        if (accountInfo && accountInfo.length > 0) {
          logger.info('✅ Account info retrieved via Farcaster resolution', {
            accountsFound: accountInfo.length,
            primaryAddress: accountInfo[0].address,
          });
          return mapAccountDataToResult(accountInfo[0], farcasterProfile);
        } else {
          logger.warn('⚠️ No account info found for Farcaster-resolved addresses', { addresses });
        }
      } else {
        logger.warn('⚠️ Failed to resolve Farcaster profile', {
          usernameProvided: useUsername || 'none',
          fidProvided: useFid || 'none',
        });
      }
    } else {
      logger.debug('🔍 No valid Farcaster identifiers to resolve');
    }
  }

  // Regular account lookup
  const query = `
    query GetAccountInfo(
      $addresses: [Address!]
      $ens: [String!]
      $basenames: [String!]
    ) {
      accounts(
        addresses: $addresses
        ens: $ens
        basenames: $basenames
      ) {
        address
        displayName {
          value
          source
        }
        avatar {
          value {
            ... on AvatarUrl {
              url
            }
            ... on NftToken {
              metadata {
                uri
                data
              }
            }
          }
          source
        }
        description {
          value
          source
        }
        socialLinks {
          url
          name
          source
        }
        ensRecord {
          name
          metadata {
            description
            avatar {
              ... on AvatarUrl {
                url
              }
              ... on NftToken {
                metadata {
                  uri
                  data
                }
              }
            }
            github
            twitter
            email
            website
          }
        }
        basename
        lensProfile {
          handle
        }
        farcasterProfile {
          username
          fid
          custodyAddress
          connectedAddresses
          metadata {
            displayName
            description
            imageUrl
            warpcast
            xAccount {
              username
              url
            }
          }
          followStats {
            followerCount
            followingCount
          }
        }
        followStats {
          followerCount
          followingCount
        }
        isContract
        contract {
          address
          network
          app {
            displayName
          }
        }
      }
    }
  `;

  const variables: any = {};

  if (content.address) {
    variables.addresses = [content.address];
    logger.debug('📍 Using address for query', { addresses: variables.addresses });
  }
  if (content.ens) {
    variables.ens = [content.ens];
    logger.debug('🌐 Using ENS for query', { ens: variables.ens });
  }
  if (content.basename) {
    variables.basenames = [content.basename];
    logger.debug('🔵 Using basename for query', { basenames: variables.basenames });
  }

  logger.debug('🎯 GraphQL query variables prepared', { variables });

  try {
    logger.debug('📡 Executing GraphQL query for account info');

    // Use the optimized client with caching, rate limiting, and error handling
    const result = await client.query(query, variables, {
      cacheTTL: 600000, // 10 minutes cache for account data (relatively stable)
      useCache: true,
      timeout: 30000,
      retryAttempts: 3,
    });

    logger.debug('📊 GraphQL query completed', {
      hasData: !!result.data,
      hasErrors: !!result.errors,
      accountsCount: result.data?.accounts?.length || 0,
    });

    if (result.errors) {
      logger.error('❌ Account info GraphQL errors detected', {
        errors: result.errors,
        variables,
      });
      throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
    }

    if (result.data?.accounts && result.data.accounts.length > 0) {
      logger.info('✅ Account data retrieved successfully', {
        accountsFound: result.data.accounts.length,
        primaryAddress: result.data.accounts[0].address,
        hasDisplayName: !!result.data.accounts[0].displayName?.value,
        hasEns: !!result.data.accounts[0].ensRecord?.name,
        hasBasename: !!result.data.accounts[0].basename,
        hasFarcaster: !!result.data.accounts[0].farcasterProfile,
      });
      return mapAccountDataToResult(result.data.accounts[0]);
    }

    logger.warn('⚠️ No accounts found in GraphQL response', { variables });
    return null;
  } catch (error) {
    logger.error('❌ Account info API request failed', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      variables,
    });
    throw error;
  }
}

async function fetchFarcasterProfile(
  config: ZapperConfig,
  username?: string,
  fid?: number | string
): Promise<FarcasterProfileData | null> {
  logger.info('🟣 Fetching Farcaster profile', { username, fid });

  const client = getZapperClient();

  const query = `
    query GetFarcasterProfile($username: String, $fid: Int) {
      farcasterProfile(username: $username, fid: $fid) {
        username
        fid
        custodyAddress
        connectedAddresses
        metadata {
          displayName
          description
          imageUrl
          warpcast
          xAccount {
            username
            url
          }
        }
        followStats {
          followerCount
          followingCount
        }
      }
    }
  `;

  // CRITICAL: Zapper API requires exactly one of fid OR username, not both
  const variables: any = {};

  // Priority: FID over username (FID is more precise)
  if (fid) {
    variables.fid = typeof fid === 'string' ? parseInt(fid, 10) : fid;
    logger.debug('🆔 Using FID for Farcaster query (priority over username)', {
      fid: variables.fid,
      usernameIgnored: username || 'none',
    });
  } else if (username) {
    variables.username = username;
    logger.debug('👤 Using username for Farcaster query', { username });
  } else {
    logger.error('❌ No valid Farcaster identifier provided');
    throw new Error('Either fid or username must be provided for Farcaster profile lookup');
  }

  logger.debug('🎯 Farcaster query variables prepared', { variables });

  try {
    logger.debug('📡 Executing Farcaster profile GraphQL query');

    const result = await client.query(query, variables, {
      cacheTTL: 600000, // 10 minutes cache for Farcaster profile data
      useCache: true,
      timeout: 30000,
      retryAttempts: 3,
    });

    logger.debug('📊 Farcaster GraphQL query completed', {
      hasData: !!result.data,
      hasErrors: !!result.errors,
      hasProfile: !!result.data?.farcasterProfile,
    });

    if (result.errors) {
      logger.error('❌ Farcaster profile GraphQL errors detected', {
        errors: result.errors,
        variables,
      });
      throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
    }

    const profile = result.data?.farcasterProfile || null;
    if (profile) {
      logger.info('✅ Farcaster profile retrieved successfully', {
        username: profile.username,
        fid: profile.fid,
        displayName: profile.metadata?.displayName,
        hasConnectedAddresses: !!profile.connectedAddresses?.length,
      });
    } else {
      logger.warn('⚠️ No Farcaster profile found', { variables });
    }

    return profile;
  } catch (error) {
    logger.error('❌ Farcaster profile API request failed', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      variables,
    });
    throw error;
  }
}

async function fetchAccountsByAddresses(
  config: ZapperConfig,
  addresses: string[]
): Promise<AccountInfoData[] | null> {
  logger.info('📍 Fetching accounts by addresses', {
    addressCount: addresses.length,
    addresses: addresses.slice(0, 3), // Log first 3 addresses for debugging
  });

  const client = getZapperClient();

  const query = `
    query GetAccountsByAddresses($addresses: [Address!]!) {
      accounts(addresses: $addresses) {
        address
        displayName {
          value
          source
        }
        avatar {
          value {
            ... on AvatarUrl {
              url
            }
            ... on NftToken {
              metadata {
                uri
                data
              }
            }
          }
          source
        }
        description {
          value
          source
        }
        socialLinks {
          url
          name
          source
        }
        ensRecord {
          name
          metadata {
            description
            avatar {
              ... on AvatarUrl {
                url
              }
              ... on NftToken {
                metadata {
                  uri
                  data
                }
              }
            }
            github
            twitter
            email
            website
          }
        }
        basename
        followStats {
          followerCount
          followingCount
        }
        isContract
      }
    }
  `;

  try {
    logger.debug('📡 Executing GraphQL query for accounts by addresses');

    const result = await client.query(
      query,
      { addresses },
      {
        cacheTTL: 600000, // 10 minutes cache for account data by addresses
        useCache: true,
        timeout: 30000,
        retryAttempts: 3,
      }
    );

    logger.debug('📊 Accounts by addresses GraphQL query completed', {
      hasData: !!result.data,
      hasErrors: !!result.errors,
      accountsFound: result.data?.accounts?.length || 0,
    });

    if (result.errors) {
      logger.error('❌ Accounts by addresses GraphQL errors detected', {
        errors: result.errors,
        addresses,
      });
      throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
    }

    const accounts = result.data?.accounts || null;
    if (accounts && accounts.length > 0) {
      logger.info('✅ Accounts retrieved successfully by addresses', {
        accountsFound: accounts.length,
        firstAccountAddress: accounts[0].address,
      });
    } else {
      logger.warn('⚠️ No accounts found for provided addresses', { addresses });
    }

    return accounts;
  } catch (error) {
    logger.error('❌ Accounts by addresses API request failed', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      addresses,
    });
    throw error;
  }
}

function mapAccountDataToResult(
  account: AccountInfoData,
  farcasterProfile?: FarcasterProfileData
): AccountResolutionResult {
  logger.debug('🎨 Mapping account data to result', {
    address: account.address,
    hasDisplayName: !!account.displayName?.value,
    hasEns: !!account.ensRecord?.name,
    hasBasename: !!account.basename,
    hasFarcasterProfile: !!account.farcasterProfile,
    hasProvidedFarcasterProfile: !!farcasterProfile,
    hasAvatar: !!account.avatar,
    isContract: account.isContract,
  });

  let avatar: string | undefined;

  const avatarValue = account.avatar?.value;
  if (avatarValue && typeof avatarValue === 'object') {
    const avatarObj = avatarValue as Record<string, any>;
    if ('url' in avatarObj && avatarObj.url) {
      avatar = avatarObj.url;
      logger.debug('🖼️ Avatar URL extracted from account data', { avatar });
    } else if ('image' in avatarObj && avatarObj.image) {
      avatar = avatarObj.image;
      logger.debug('🖼️ Avatar image extracted from account data', { avatar });
    }
  }

  const result = {
    address: account.address,
    ens: account.ensRecord?.name,
    basename: account.basename,
    farcasterUsername: account.farcasterProfile?.username || farcasterProfile?.username,
    fid: account.farcasterProfile?.fid || farcasterProfile?.fid,
    displayName: account.displayName.value,
    avatar,
    description: account.description?.value,
    socialLinks: account.socialLinks || [],
    followStats: account.followStats || farcasterProfile?.followStats,
    isContract: account.isContract,
    badges: account.badges,
  };

  logger.info('✅ Account data mapping completed', {
    address: result.address,
    displayName: result.displayName,
    hasEns: !!result.ens,
    hasBasename: !!result.basename,
    hasFarcaster: !!result.farcasterUsername,
    socialLinksCount: result.socialLinks.length,
    isContract: result.isContract,
  });

  return result;
}

/**
 * Extract account identifier using LLM - simple and accurate
 */
async function extractAccountIdentifier(
  runtime: IAgentRuntime,
  text: string,
  state?: State
): Promise<{ value: string; type: string } | null> {
  logger.info('🔍 Starting LLM-based account identifier extraction', {
    textLength: text?.length || 0,
    textPreview: text?.substring(0, 50) + '...',
    hasState: !!state,
  });

  // Prepare the context for LLM
  let contextText = text;
  if (state?.recentMessages) {
    contextText = `Current message: ${text}\n\nRecent messages: ${state.recentMessages}`;
  }

  const extractionPrompt = `Extract any account identifier from this text. Look for:
- Ethereum addresses (0x followed by 40 characters)
- ENS names (ending with .eth)
- Base names (ending with .base) 
- Farcaster usernames (like @username or username)
- Farcaster IDs (FID numbers)

Text to analyze:
${contextText}

If you find an identifier, respond with ONLY this format:
TYPE:VALUE

Where TYPE is one of: ADDRESS, ENS, BASENAME, FARCASTER_USERNAME, FARCASTER_FID
And VALUE is the actual identifier found.

If no identifier is found, respond with: NONE

Examples:
- ADDRESS:0x1234567890abcdef1234567890abcdef12345678
- ENS:vitalik.eth
- BASENAME:alice.base
- FARCASTER_USERNAME:dwr
- FARCASTER_FID:3
- NONE`;

  try {
    logger.debug('🤖 Sending extraction request to LLM');

    const result = await runtime.useModel(ModelType.TEXT_SMALL, {
      prompt: extractionPrompt,
      stopSequences: [],
    });

    const response = result?.trim();
    logger.debug('📥 LLM response received', { response });

    if (!response || response === 'NONE') {
      logger.info('❌ No identifier found by LLM');
      return null;
    }

    // Parse the response
    const parts = response.split(':');
    if (parts.length !== 2) {
      logger.warn('⚠️ Invalid LLM response format', { response });
      return null;
    }

    const [type, value] = parts;
    const validTypes = ['ADDRESS', 'ENS', 'BASENAME', 'FARCASTER_USERNAME', 'FARCASTER_FID'];

    if (!validTypes.includes(type)) {
      logger.warn('⚠️ Invalid identifier type from LLM', { type, value });
      return null;
    }

    logger.info('✅ Identifier extracted successfully by LLM', { type, value });
    return { type, value };
  } catch (error) {
    logger.error('❌ Error in LLM identifier extraction', {
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

/**
 * Create content object from identifier
 */
function createContentFromIdentifier(identifier: {
  value: string;
  type: string;
}): AccountInfoContent {
  logger.info('🔄 Creating content from identifier', identifier);

  const content: any = {};

  switch (identifier.type) {
    case 'ADDRESS':
      content.address = identifier.value;
      break;
    case 'ENS':
      content.ens = identifier.value;
      break;
    case 'BASENAME':
      content.basename = identifier.value;
      break;
    case 'FARCASTER_USERNAME':
      content.farcasterUsername = identifier.value;
      break;
    case 'FARCASTER_FID':
      content.fid = identifier.value;
      break;
    default:
      logger.warn('⚠️ Unknown identifier type', { type: identifier.type });
  }

  logger.debug('✅ Content created', { content });
  return content;
}
